import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { getMyFamily } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import {
  Users, Copy, Check, ShieldCheck, UserCircle, Calendar, Home as HomeIcon, Sparkles,
} from 'lucide-react';

const colorFromName = (name) => {
  const palette = [
    'bg-emerald-500', 'bg-blue-500', 'bg-amber-500', 'bg-purple-500',
    'bg-rose-500', 'bg-cyan-500', 'bg-orange-500', 'bg-indigo-500',
  ];
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h + name.charCodeAt(i)) % palette.length;
  return palette[h];
};

const initials = (name) =>
  name.split(/\s+/).map((w) => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();

const FamilyPage = () => {
  const { user } = useAuth();
  const [family, setFamily] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await getMyFamily();
      setFamily(res.data);
    } catch (e) {
      toast.error('Errore caricamento famiglia');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const copyCode = () => {
    if (!family) return;
    navigator.clipboard.writeText(family.code);
    setCopied(true);
    toast.success('Codice copiato');
    setTimeout(() => setCopied(false), 1800);
  };

  if (loading || !family) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-8 bg-muted rounded w-48" />
        <div className="h-32 bg-muted rounded-xl" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {[1, 2].map((i) => <div key={i} className="h-24 bg-muted rounded-xl" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-in" data-testid="family-page">
      <div>
        <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
          <HomeIcon className="w-7 h-7 text-primary" strokeWidth={1.5} />
          La mia famiglia
        </h1>
        <p className="text-muted-foreground">
          Tutti i membri condividono budget, transazioni e obiettivi (eccetto quelli marcati come privati).
        </p>
      </div>

      {/* Family code card */}
      <Card className="bg-gradient-to-br from-primary/5 to-primary/10 border-primary/20">
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-primary/15 flex items-center justify-center shrink-0">
              <Sparkles className="w-7 h-7 text-primary" strokeWidth={1.5} />
            </div>
            <div className="flex-1">
              <p className="text-xs uppercase tracking-wide text-muted-foreground">Codice famiglia</p>
              <p className="font-mono text-3xl font-extrabold tracking-widest text-foreground">{family.code}</p>
              <p className="text-xs text-muted-foreground mt-1">
                Chi conosce questo codice può unirsi alla tua famiglia in fase di registrazione.
              </p>
            </div>
            <Button onClick={copyCode} variant="outline" data-testid="copy-family-code-btn">
              {copied ? <Check className="w-4 h-4 mr-2" /> : <Copy className="w-4 h-4 mr-2" />}
              {copied ? 'Copiato' : 'Copia codice'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Members */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Users className="w-5 h-5" />
            Membri ({family.member_count})
          </CardTitle>
          <CardDescription>
            Tutti i componenti collegati allo stesso codice famiglia.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {family.members.map((m) => {
              const isMe = m.is_self;
              return (
                <div
                  key={m.id}
                  className={`flex items-start gap-3 p-4 rounded-xl border transition-colors ${
                    isMe ? 'border-primary/30 bg-primary/5' : 'bg-card hover:border-primary/20'
                  }`}
                  data-testid={`member-card-${m.id}`}
                >
                  <div className={`w-12 h-12 rounded-full ${colorFromName(m.name)} text-white flex items-center justify-center font-bold shrink-0`}>
                    {initials(m.name)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-semibold truncate">{m.name}</p>
                      {isMe && <Badge className="bg-primary/15 text-primary border-0 text-[10px]">tu</Badge>}
                      {m.role === 'admin' && (
                        <Badge className="bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200 border-0 text-[10px]">
                          <ShieldCheck className="w-3 h-3 mr-0.5" /> admin
                        </Badge>
                      )}
                      {m.is_creator && (
                        <Badge className="bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200 border-0 text-[10px]">
                          fondatore
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground truncate">{m.email}</p>
                    {m.created_at && (
                      <p className="text-[11px] text-muted-foreground mt-1 inline-flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        Iscritto il {new Date(m.created_at).toLocaleDateString('it-IT')}
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Note privacy */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <UserCircle className="w-4 h-4 text-primary" />
            Privacy & area personale
          </CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground space-y-2">
          <p>
            Quando aggiungi una transazione, un budget o un obiettivo puoi attivare lo switch{' '}
            <span className="font-medium text-foreground">"Privato"</span> per renderlo visibile solo a te.
            Gli altri membri non lo vedranno né lo contano nel saldo condiviso.
          </p>
          <p>
            Visita la pagina <span className="font-medium text-foreground">"Area personale"</span> dalla sidebar per gestire e rivedere i tuoi movimenti privati.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default FamilyPage;
